import { useMemo, useState } from 'react';
import {
  Landmark,
  CreditCard,
  Megaphone,
  ShoppingCart,
  Download,
  Check,
  X,
  Save,
  Settings,
  FileText,
} from 'lucide-react';
import { EmptyState } from '@/components/shared/empty-state';
import { PageHeader } from '@/components/shared/page-header';
import { Button } from '@/components/ui/button';
import { MonthInput } from '@/components/ui/month-input';
import { useLocationStore } from '@/stores/location-store';
import { usePermissions } from '@/hooks/use-permissions';
import { rpApi, posBackendApi } from '@/lib/api-client';
import { PosReportCardItem, type PosReportCard } from '@/pages/reports';
import {
  useEffectiveReportHidden,
  useReportVisibilityMatrix,
  useUpdateReportVisibility,
} from '@/hooks/use-report-visibility';
import { toast } from '@/stores/toast-store';
import { cn } from '@/lib/utils';

const ACH_STATEMENT_CARD: PosReportCard = {
  id: 'ach-statement',
  title: 'ACH Collections & Payments Statement',
  description: 'Generate a PDF statement of ACH collections, outbound payments, fees, and net position.',
  icon: Landmark,
};

const CARD_STATEMENT_CARD: PosReportCard = {
  id: 'card-statement',
  title: 'Card Statement',
  description: 'Generate a PDF statement of card sales.',
  icon: CreditCard,
};

const CAMPAIGN_STATEMENT_CARD: PosReportCard = {
  id: 'campaign-statement',
  title: 'Campaign Subscription Fee Statement',
  description: 'Generate a PDF statement of campaign SMS usage and subscription fees.',
  icon: Megaphone,
};

const POS_STATEMENT_CARD: PosReportCard = {
  id: 'pos-statement',
  title: 'POS Statement',
  description: 'Generate a PDF statement of POS platform fees: lanes, online checkout, locations, and warehouses.',
  icon: ShoppingCart,
};

function MonthlyStatementDetail({
  title,
  description,
  onBack,
  endpoint,
  filenamePrefix,
  downloadPdf,
}: {
  title: string;
  description: string;
  onBack: () => void;
  endpoint: string;
  filenamePrefix: string;
  downloadPdf: (
    endpoint: string,
    params: Record<string, string | number | boolean | undefined>,
    fallbackFilename: string,
  ) => Promise<void>;
}) {
  const { selectedLocationId } = useLocationStore();
  const [monthInput, setMonthInput] = useState(''); // "YYYY-MM"
  const [isGenerating, setIsGenerating] = useState(false);

  const now = new Date();
  const lastCompletedMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  const maxMonth = `${lastCompletedMonth.getFullYear()}-${String(lastCompletedMonth.getMonth() + 1).padStart(2, '0')}`;

  const canGenerate = Boolean(monthInput);

  const handleGenerate = async () => {
    if (!canGenerate) return;
    setIsGenerating(true);
    try {
      const [year, month] = monthInput.split('-');
      const params: Record<string, string> = { year, month: String(parseInt(month, 10)) };
      if (selectedLocationId) params.locationId = selectedLocationId;

      await downloadPdf(endpoint, params, `${filenamePrefix}.pdf`);
    } catch {
      // downloadPdf already surfaces a toast on failure
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <div className="space-y-4">
      <PageHeader title={title} description={description} onBack={onBack} />

      <div className="flex flex-wrap items-center gap-3">
        <MonthInput
          value={monthInput}
          max={maxMonth}
          onChange={(e) => setMonthInput(e.target.value)}
          className="h-9 w-44 rounded-md border border-input bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
        />

        <Button size="sm" variant="primary" disabled={!canGenerate || isGenerating} onClick={handleGenerate}>
          <Download className="h-3.5 w-3.5" />
          {isGenerating ? 'Generating…' : 'Generate PDF'}
        </Button>
      </div>
    </div>
  );
}

/** The three statement cards, in menu order. Shared by the card grid and by the
 *  Settings matrix so the two can never drift. */
const STATEMENT_CARDS: { id: string; card: PosReportCard }[] = [
  { id: 'ach-statement', card: ACH_STATEMENT_CARD },
  { id: 'card-statement', card: CARD_STATEMENT_CARD },
  { id: 'campaign-statement', card: CAMPAIGN_STATEMENT_CARD },
  { id: 'pos-statement', card: POS_STATEMENT_CARD },
];
const STATEMENT_IDS = STATEMENT_CARDS.map((c) => c.id);

// Roles configurable in the matrix. super_admin is omitted — it always sees
// every statement and can never be hidden. Mirrors REPORT_VISIBILITY_ROLES.
const STATEMENT_VISIBILITY_ROLES: { id: string; label: string }[] = [
  { id: 'owner', label: 'Owner' },
  { id: 'admin', label: 'Admin' },
  { id: 'manager', label: 'Manager' },
  { id: 'employee', label: 'Employee' },
];

const cellKey = (id: string, role: string) => `${id}::${role}`;

function setsEqual(a: Set<string>, b: Set<string>): boolean {
  if (a.size !== b.size) return false;
  for (const v of a) if (!b.has(v)) return false;
  return true;
}

/**
 * Statements → Settings. Same matrix as Reports → POS → Settings: a row per
 * statement, a column per role, green check = visible.
 *
 * Shares the pos_report_visibility storage, which matters for saving: the PUT
 * REPLACES every row for the location, so the payload has to carry the POS
 * report entries through untouched. Sending only the statement rows would wipe
 * the Reports → POS matrix.
 */
function StatementsSettings() {
  const { selectedLocationId, selectedLocationName } = useLocationStore();
  const { data, isLoading } = useReportVisibilityMatrix(selectedLocationId);
  const update = useUpdateReportVisibility();

  const allHidden = useMemo(() => data?.data?.hidden ?? [], [data]);

  // Only the statement rows are editable here; everything else is passed through.
  const initialHidden = useMemo(() => {
    const s = new Set<string>();
    allHidden.filter((e) => STATEMENT_IDS.includes(e.reportId)).forEach((e) => s.add(cellKey(e.reportId, e.role)));
    return s;
  }, [allHidden]);

  const [draft, setDraft] = useState<Set<string> | null>(null);
  const hidden = draft ?? initialHidden;
  const dirty = draft !== null && !setsEqual(draft, initialHidden);

  const isVisible = (id: string, role: string) => !hidden.has(cellKey(id, role));

  const mutate = (fn: (next: Set<string>) => void) => {
    setDraft((prev) => {
      const next = new Set(prev ?? initialHidden);
      fn(next);
      return next;
    });
  };

  const toggleCell = (id: string, role: string) =>
    mutate((next) => {
      const k = cellKey(id, role);
      if (next.has(k)) next.delete(k);
      else next.add(k);
    });

  const bulkSet = (visible: boolean) =>
    mutate((next) => {
      for (const { id } of STATEMENT_CARDS) {
        for (const role of STATEMENT_VISIBILITY_ROLES) {
          const k = cellKey(id, role.id);
          if (visible) next.delete(k);
          else next.add(k);
        }
      }
    });

  const save = async () => {
    if (!selectedLocationId || !draft) return;
    // Carry the non-statement entries through untouched — see the note above.
    const preserved = allHidden.filter((e) => !STATEMENT_IDS.includes(e.reportId));
    const statementRows = Array.from(draft).map((k) => {
      const [reportId, role] = k.split('::');
      return { reportId, role };
    });
    try {
      await update.mutateAsync({ locationId: selectedLocationId, hidden: [...preserved, ...statementRows] });
      setDraft(null);
      toast.success('Statement permissions saved');
    } catch (err) {
      const message = (err as { error?: { message?: string } })?.error?.message;
      toast.error(message || 'Failed to save statement permissions');
    }
  };

  if (!selectedLocationId) {
    return (
      <div className="rounded-xl border border-dashed border-border bg-white p-12 text-center text-sm text-muted-foreground">
        Select a location to configure statement permissions.
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-1">
        <h3 className="text-sm font-semibold text-foreground">Statement Visibility</h3>
        <p className="text-xs text-muted-foreground">
          Control which roles can see each statement
          {selectedLocationName ? ` at ${selectedLocationName}` : ''}. Statements are visible by default; uncheck a role
          to hide one from it. Super Admin always has full access.
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <div className="ml-auto flex items-center gap-2">
          <button
            type="button"
            onClick={() => bulkSet(true)}
            className="h-9 rounded-lg border border-border px-3 text-xs font-medium text-foreground hover:bg-muted"
          >
            Show all
          </button>
          <button
            type="button"
            onClick={() => bulkSet(false)}
            className="h-9 rounded-lg border border-border px-3 text-xs font-medium text-foreground hover:bg-muted"
          >
            Hide all
          </button>
        </div>
      </div>

      <div className="overflow-x-auto rounded-lg border border-border bg-white">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border bg-muted/50">
              <th className="px-3 py-2.5 text-left text-xs font-medium text-muted-foreground">Statement</th>
              {STATEMENT_VISIBILITY_ROLES.map((r) => (
                <th key={r.id} className="px-3 py-2.5 text-center text-xs font-medium text-muted-foreground">
                  {r.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td
                  colSpan={1 + STATEMENT_VISIBILITY_ROLES.length}
                  className="px-3 py-8 text-center text-muted-foreground"
                >
                  Loading...
                </td>
              </tr>
            ) : (
              STATEMENT_CARDS.map(({ id, card }) => (
                <tr key={id} className="border-b border-border last:border-0 hover:bg-muted/30">
                  <td className="px-3 py-2 font-medium text-foreground">{card.title}</td>
                  {STATEMENT_VISIBILITY_ROLES.map((role) => {
                    const on = isVisible(id, role.id);
                    return (
                      <td key={role.id} className="px-3 py-2 text-center">
                        <button
                          type="button"
                          onClick={() => toggleCell(id, role.id)}
                          aria-label={`${on ? 'Hide' : 'Show'} ${card.title} for ${role.label}`}
                          className={cn(
                            'inline-flex h-7 w-7 items-center justify-center rounded-md border transition-colors',
                            on
                              ? 'border-green-300 bg-green-50 text-green-700 hover:bg-green-100'
                              : 'border-red-200 bg-red-50 text-red-600 hover:bg-red-100',
                          )}
                        >
                          {on ? <Check className="h-4 w-4" /> : <X className="h-4 w-4" />}
                        </button>
                      </td>
                    );
                  })}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={save}
          disabled={!dirty || update.isPending}
          className="inline-flex h-9 items-center gap-2 rounded-lg bg-primary px-4 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
        >
          <Save className="h-3.5 w-3.5" />
          {update.isPending ? 'Saving...' : 'Save changes'}
        </button>
        {dirty && (
          <button
            type="button"
            onClick={() => setDraft(null)}
            className="h-9 rounded-lg border border-border px-4 text-sm font-medium text-foreground hover:bg-muted"
          >
            Cancel
          </button>
        )}
      </div>
    </div>
  );
}

export function StatementsPage() {
  const [activeReportId, setActiveReportId] = useState<string | null>(null);
  const [showSettings, setShowSettings] = useState(false);
  const { selectedLocationId } = useLocationStore();
  // Statement settings stay Owner only (plus platform super admins), like
  // Reports → POS → Settings; the page itself opens with statements.view.
  const canAdminister = usePermissions().atLeast('owner');

  // Cards this user may see, per the same matrix Settings edits. Cosmetic
  // filtering only — the statement endpoints live in receivepayment and
  // pos-backend and enforce their own auth.
  const { data: hiddenData } = useEffectiveReportHidden(selectedLocationId);
  const hiddenIds = new Set(hiddenData?.data?.hiddenReportIds ?? []);
  const visibleCards = STATEMENT_CARDS.filter(({ id }) => !hiddenIds.has(id));

  if (activeReportId === 'ach-statement') {
    return (
      <MonthlyStatementDetail
        title={ACH_STATEMENT_CARD.title}
        description={ACH_STATEMENT_CARD.description}
        onBack={() => setActiveReportId(null)}
        endpoint="/ach/statement"
        filenamePrefix="ACH_Statement"
        downloadPdf={rpApi.downloadPdf}
      />
    );
  }

  if (activeReportId === 'card-statement') {
    return (
      <MonthlyStatementDetail
        title={CARD_STATEMENT_CARD.title}
        description={CARD_STATEMENT_CARD.description}
        onBack={() => setActiveReportId(null)}
        endpoint="/card/statement"
        filenamePrefix="Card_Statement"
        downloadPdf={rpApi.downloadPdf}
      />
    );
  }

  if (activeReportId === 'pos-statement') {
    return (
      <MonthlyStatementDetail
        title={POS_STATEMENT_CARD.title}
        description={POS_STATEMENT_CARD.description}
        onBack={() => setActiveReportId(null)}
        endpoint="/statements/pos"
        filenamePrefix="POS_Statement"
        downloadPdf={posBackendApi.downloadPdf}
      />
    );
  }

  if (activeReportId === 'campaign-statement') {
    return (
      <MonthlyStatementDetail
        title={CAMPAIGN_STATEMENT_CARD.title}
        description={CAMPAIGN_STATEMENT_CARD.description}
        onBack={() => setActiveReportId(null)}
        endpoint="/customer-engagement/campaigns/statement"
        filenamePrefix="Campaign_Subscription_Fee_Statement"
        downloadPdf={posBackendApi.downloadPdf}
      />
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader title="Statements" description="Generate account statements and financial documents." />

      {/* Settings tab — owner only, mirroring Reports → POS → Settings. Rendered
          as a tab bar only when the user can administer, so everyone else sees
          the cards exactly as before. */}
      {canAdminister && (
        <div className="border-b border-border">
          <div className="flex w-full min-w-0" role="tablist" aria-label="Statements sections">
            <button
              type="button"
              role="tab"
              aria-selected={!showSettings}
              onClick={() => setShowSettings(false)}
              className={cn(
                'relative shrink-0 px-4 py-2.5 text-sm font-medium transition-colors',
                !showSettings ? 'text-primary' : 'text-muted-foreground hover:text-foreground',
              )}
            >
              Statements
              {!showSettings && <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary" />}
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={showSettings}
              onClick={() => setShowSettings(true)}
              className={cn(
                'relative flex shrink-0 items-center gap-1.5 px-4 py-2.5 text-sm font-medium transition-colors',
                showSettings ? 'text-primary' : 'text-muted-foreground hover:text-foreground',
              )}
            >
              <Settings className="h-3.5 w-3.5" />
              Settings
              {showSettings && <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary" />}
            </button>
          </div>
        </div>
      )}

      {showSettings && canAdminister ? (
        <StatementsSettings />
      ) : visibleCards.length === 0 ? (
        <div className="rounded-xl border border-dashed border-border bg-white">
          <EmptyState
            icon={FileText}
            title="No statements available"
            description="Statements aren't available for your role at this location."
          />
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3">
          {visibleCards.map(({ id, card }) => (
            <PosReportCardItem key={id} card={card} onClick={() => setActiveReportId(id)} />
          ))}
        </div>
      )}
    </div>
  );
}
